import { LoanType } from "@/modules/loan/common/enum";
import { useGetLoanByIdQuery } from "@/modules/loan/services";
import { AppPaths, CloudflareAsset, SideSheetPosition } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { DetailActions, EmptyOrErrorContent } from "@/shared/components";
import { buildRoute, getErrorMessage } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { LoanDetailHistory } from "./LoanDetailHistory";
import { LoanDetailRows } from "./LoanDetailRows";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  loanId: string;
}

export const LoanDetails = ({ isOpen, onClose, loanId }: Props) => {
  const navigate = useNavigate();
  const featureToggle = useAppSelector(state => state.featureToggle.toggles);
  const isDesktop = useMediaQuery();
  const { t } = useTranslation();

  const {
    data: loan,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetLoanByIdQuery({ id: loanId });

  const navigateToStatementPage = () => {
    const url = buildRoute(AppPaths.LoanStatement, { loanId });
    navigate(url);
  };

  const navigateToSchedulePage = () => {
    const url = buildRoute(AppPaths.LoanSchedule, { loanId });
    navigate(url);
  };

  const navigateToLoanRepaymentPage = () => {
    if (!loan?.contractNumber) return;
    const url = `${AppPaths.LoanRepayment}?loanContractNumber=${loan.contractNumber}`;
    navigate(url);
  };

  const navigateToOwnAccountPage = () => {
    if (!loan?.settlementAccount) return;
    const url = `${AppPaths.OwnAccountTransfer}?toAccount=${loan.settlementAccount}`;
    navigate(url);
  };

  const isOverdraft = loan?.loanType === LoanType.Overdraft;
  const isStandard = loan?.loanType === LoanType.Standard;
  const isCreditLine = loan?.loanType === LoanType.CreditLine;

  const actions = [
    {
      id: 0,
      title: t("general.repay"),
      icon: CloudflareAsset.LoanRepayment,
      isAvailable: featureToggle.isLoanRepaymentAvailable && (isStandard || isCreditLine),
      onClick: () => navigateToLoanRepaymentPage(),
    },
    {
      id: 1,
      title: t("general.topUp"),
      icon: CloudflareAsset.TopUp,
      isAvailable: featureToggle.isOwnAccountTransferAvailable && isOverdraft,
      onClick: () => navigateToOwnAccountPage(),
    },
    {
      id: 2,
      title: t("general.statement.title"),
      icon: CloudflareAsset.DocumentDeclaration,
      isAvailable: featureToggle.isLoanStatementAvailable,
      onClick: () => navigateToStatementPage(),
    },
    {
      id: 3,
      title: t("general.schedule.title"),
      icon: CloudflareAsset.DocumentList,
      isAvailable: featureToggle.isLoanScheduleAvailable && !isOverdraft,
      onClick: () => navigateToSchedulePage(),
    }
  ].filter((action) => action.isAvailable);

  return (
    <SideSheet
      position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
      isPositioned={!isDesktop}
      withOverlay={!isDesktop}
      isOpen={isOpen}
      isBodyHidden={false}
      onClose={onClose}
      isWithDivider
      isLoading={isFetching}
      className="detail-side-sheet"
      size="small"
      title={loan?.name || loan?.contractNumber}
      shouldRemoveCallback={false}
      closeOnOutsideClick={false}
    >
      {({ scrollbarContainerRef }) => (
        <>
          {isFetching ? (
            <Spinner isFullScreen={false} size="large"/>
          ) : isError && !isFetching ? (
            <div className="full-width full-height flexbox align-items--center">
              <EmptyOrErrorContent
                isError={true}
                title={t(getErrorMessage(error as IError))}
                action={refetch}
              />
            </div>
          ) : (
            <>
              <LoanDetailRows
                loan={loan}
                scrollbarContainerRef={scrollbarContainerRef}
              />
              <DetailActions actions={actions} page="Loan detail"/>
              {loan?.contractNumber && (
                <LoanDetailHistory
                  loanId={loanId}
                  id={`${loan?.id}`}
                />
              )}
            </>
          )}
        </>
      )}
    </SideSheet>
  );
};
